/** "Reset sandbox" (S5a; SBX D4 §7) and the content clone's report (D3 "Clone from production").
 *
 *  A reset wipes the sandbox's bookings, tour users, chats and outbox in a task on its own lane, and
 *  keeps its config, keys, staff and content; it is refused while the sandbox is suspended (its queued
 *  work would be dropped). The clone report says what was copied from production after creation,
 *  which notifications were copied switched off (they named production departures or tags) and what
 *  was left out. */
import { Alert, Box, Button, Chip, Stack, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import {
  sandboxApi,
  sandboxErrorMessage,
  type SandboxCloneReport,
  type SandboxResetState,
} from '@/api/sandbox';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useSnackbar } from '@/components/Snackbar';

import { When } from './When';

const RESET_STATUS_LABEL: Record<string, string> = {
  queued: 'Queued',
  continuing: 'Running',
  done: 'Done',
};

function counts(map: Record<string, number> | undefined): string {
  const parts = Object.entries(map ?? {})
    .filter(([, n]) => n > 0)
    .map(([k, n]) => `${n.toLocaleString()} ${k.replace(/_/g, ' ')}`);
  return parts.length > 0 ? parts.join(', ') : 'nothing';
}

export function ResetSection({
  topId,
  code,
  reset,
  suspended,
  canManage,
}: {
  topId: number;
  code: string;
  reset: SandboxResetState | null | undefined;
  suspended: boolean;
  canManage: boolean;
}) {
  const qc = useQueryClient();
  const snackbar = useSnackbar();
  const [confirm, setConfirm] = useState(false);
  const run = useMutation({
    mutationFn: () => sandboxApi.reset(topId),
    onSuccess: () => {
      snackbar.show(
        'Reset queued: the sandbox’s bookings, tour users, chats and outbox are being removed.',
        'success',
      );
      void qc.invalidateQueries({ queryKey: ['tourOperators', 'sandbox', topId] });
    },
    onError: (e) => snackbar.show(sandboxErrorMessage(e, 'Reset failed.'), 'error'),
  });
  const running = reset?.status === 'queued' || reset?.status === 'continuing';
  const blocked = suspended
    ? 'Resume the sandbox first: a suspended sandbox’s queued work is dropped.'
    : running
      ? 'A reset is running.'
      : null;
  return (
    <Stack spacing={1.5}>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '80ch' }}>
        Removes the sandbox’s bookings, tour users, chats and outbox. Its configuration, keys,
        staff, catalog and notifications stay. Bookings older than 30 days and outbox entries older
        than 14 days are purged nightly anyway.
      </Typography>
      {reset && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            size="small"
            variant="outlined"
            label={RESET_STATUS_LABEL[reset.status ?? ''] ?? reset.status ?? 'Unknown'}
          />
          <Typography variant="body2" color="text.secondary" component="div">
            Last reset requested <When iso={reset.requested_at} />
            {reset.requested_by ? ` by ${reset.requested_by}` : ''}
            {reset.status === 'done' && (
              <>
                {', finished '}
                <When iso={reset.finished_at} />: removed {counts(reset.counts)}
              </>
            )}
            .
          </Typography>
        </Box>
      )}
      {canManage && (
        <Box>
          <Button
            color="error"
            variant="outlined"
            disabled={Boolean(blocked) || run.isPending}
            onClick={() => setConfirm(true)}
          >
            Reset sandbox…
          </Button>
          {blocked && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1.5 }}>
              {blocked}
            </Typography>
          )}
        </Box>
      )}
      <ConfirmDialog
        open={confirm}
        title={`Reset ${code}`}
        message={`Every booking, tour user, chat and outbox entry of ${code} is removed. Its configuration, keys, staff and content stay. This cannot be undone.`}
        confirmLabel="Reset sandbox"
        destructive
        onConfirm={() => {
          setConfirm(false);
          run.mutate();
        }}
        onCancel={() => setConfirm(false)}
      />
    </Stack>
  );
}

/** The first five entries, named, then "…". */
function listOf(
  items: Record<string, unknown>[],
  name: (item: Record<string, unknown>) => string,
): string {
  const shown = items.slice(0, 5).map(name).join(', ');
  return items.length > 5 ? `${shown}, …` : shown;
}

const CLONE_STATUS_LABEL: Record<string, string> = {
  queued: 'Queued',
  running: 'Copying',
  done: 'Copied',
  failed: 'Failed',
};

/** What was copied from production (`config.sandbox.clone`). */
export function CloneReport({ clone }: { clone: SandboxCloneReport }) {
  const disabled = clone.disabled ?? [];
  const skipped = clone.skipped ?? [];
  return (
    <Stack spacing={1}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Chip
          size="small"
          variant="outlined"
          color={
            clone.status === 'failed' ? 'error' : clone.status === 'done' ? 'success' : 'default'
          }
          label={CLONE_STATUS_LABEL[clone.status ?? ''] ?? clone.status ?? 'Unknown'}
        />
        <Typography variant="body2" color="text.secondary" component="div">
          {clone.status === 'done' && (
            <>
              Finished <When iso={clone.finished_at} />: copied {counts(clone.copied)}
              {Object.keys(clone.matched ?? {}).length > 0 &&
                `; matched existing ${counts(clone.matched)}`}
              .
            </>
          )}
          {clone.status === 'failed' &&
            `Stopped: ${clone.error ?? 'unknown error'}. Nothing of that run was kept.`}
          {(clone.status === 'queued' || clone.status === 'running') &&
            'Production’s notifications, templates, strings, tag categories and launch policies are being copied.'}
        </Typography>
      </Box>
      {disabled.length > 0 && (
        <Alert severity="info">
          {disabled.length} notification{disabled.length === 1 ? ' was' : 's were'} copied switched
          off — they named production departures or tags that cannot be copied:{' '}
          {listOf(disabled, (d) => String(d.name || `#${String(d.copy_id ?? d.id)}`))}. Review and
          enable them in the sandbox.
        </Alert>
      )}
      {skipped.length > 0 && (
        <Alert severity="warning">
          {skipped.length} item{skipped.length === 1 ? ' was' : 's were'} left out because copying
          failed: {listOf(skipped, (s) => `${String(s.model ?? '?')} #${String(s.id ?? '?')}`)}.
        </Alert>
      )}
    </Stack>
  );
}
